// =============================================================================
// Settings.jsx - Main Settings Shell Component (FIXED - UUID-Based Teams)
// =============================================================================
// Orchestrates all settings components and manages shared state
// NOW SUPPORTS: Teams stored as object with STABLE UUIDs as keys
// ADMIN-ONLY: Non-admin users are redirected to menu
// MULTI-FACILITY: Includes FacilityManagement component
// =============================================================================

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../utils/firebase';
import { doc, getDoc, setDoc, deleteField, collection, getDocs, writeBatch } from 'firebase/firestore';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import logo from '../assets/images/logo2.PNG';

// Import extracted components
import ClubProfile from './settings/ClubProfile';
import TeamManagement from './settings/TeamManagement';
import FacilityManagement from './settings/FacilityManagement';
import PitchConfiguration from './settings/PitchConfiguration';
import SharedFacility from './settings/components/SharedFacility';
import FeedbackSystem, { FeedbackModal } from './settings/FeedbackSystem';
import SettingsSkeleton from './settings/SettingsSkeleton';
import UserManagement from './settings/UserManagement';

import SubscriptionManagement from './settings/SubscriptionManagement';
import SupportContact from './settings/SupportContact';

// =============================================================================
// TEAM MIGRATION COMPONENT (TEMPORARY - Remove after migration)
// =============================================================================
// This component helps migrate teams from name-based keys to UUID-based keys
// Once all clubs are migrated, this entire section can be removed
// =============================================================================

const TeamMigrationTool = ({ clubId, onMigrationComplete }) => {
  const [status, setStatus] = useState('checking'); // checking, needed, migrating, complete, error
  const [logs, setLogs] = useState([]);
  const [needsMigration, setNeedsMigration] = useState(false);
  const [teamsToMigrate, setTeamsToMigrate] = useState(0);

  const addLog = (message) => {
    setLogs(prev => [...prev, message]);
    console.log('[Migration]', message);
  };

  // Check Firestore DIRECTLY to see if migration is needed
  useEffect(() => {
    const checkMigrationNeeded = async () => {
      if (!clubId) {
        setStatus('complete');
        return;
      }

      try {
        addLog('🔍 Checking Firestore for team key format...');
        const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        const settingsSnap = await getDoc(settingsRef);

        if (!settingsSnap.exists()) {
          addLog('No settings document found');
          setStatus('complete');
          return;
        }

        const data = settingsSnap.data();
        const teamsData = data.teams;

        if (!teamsData || typeof teamsData !== 'object') {
          addLog('No teams found');
          setStatus('complete');
          return;
        }

        // Check the ACTUAL Firestore keys (not the converted state)
        const teamKeys = Object.keys(teamsData);
        const keysNeedingMigration = teamKeys.filter(key => !key.startsWith('team_'));

        addLog(`📋 Found ${teamKeys.length} teams in Firestore`);
        addLog(`🔑 Keys needing migration: ${keysNeedingMigration.length}`);

        if (keysNeedingMigration.length > 0) {
          addLog(`⚠️ Teams with name-based keys: ${keysNeedingMigration.slice(0, 5).join(', ')}${keysNeedingMigration.length > 5 ? '...' : ''}`);
          setNeedsMigration(true);
          setTeamsToMigrate(keysNeedingMigration.length);
          setStatus('needed');
        } else {
          addLog('✅ All teams already have UUID keys');
          setNeedsMigration(false);
          setStatus('complete');
        }
      } catch (error) {
        addLog(`❌ Error checking migration status: ${error.message}`);
        setStatus('error');
      }
    };

    checkMigrationNeeded();
  }, [clubId]);

  const runMigration = async () => {
    if (!clubId) {
      addLog('❌ No club ID available');
      setStatus('error');
      return;
    }

    setStatus('migrating');
    setLogs([]);
    addLog('🚀 Starting team UUID migration...');

    try {
      const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
      const settingsSnap = await getDoc(settingsRef);

      if (!settingsSnap.exists()) {
        addLog('❌ Settings document not found');
        setStatus('error');
        return;
      }

      const currentData = settingsSnap.data();
      const currentTeams = currentData.teams;

      if (!currentTeams || typeof currentTeams !== 'object') {
        addLog('❌ No teams found in settings');
        setStatus('error');
        return;
      }

      const teamKeys = Object.keys(currentTeams);
      addLog(`📋 Found ${teamKeys.length} teams`);

      // Build new teams object with UUIDs
      const newTeams = {};
      const deletions = {};

      for (const oldKey of teamKeys) {
        const teamData = currentTeams[oldKey];
        
        if (oldKey.startsWith('team_')) {
          // Already has UUID, keep as is
          newTeams[oldKey] = teamData;
          addLog(`   ⏭️ ${teamData.name || oldKey} (already migrated)`);
        } else {
          // Generate new UUID
          const newUUID = generateUUID();
          
          // Small delay to ensure unique timestamps
          await new Promise(r => setTimeout(r, 10));

          newTeams[newUUID] = {
            ...teamData,
            id: newUUID,
            name: teamData.name || oldKey
          };
          
          // Mark old key for deletion
          deletions[oldKey] = deleteField();
          
          addLog(`   ✓ ${teamData.name || oldKey} → ${newUUID}`);
        }
      }

      addLog('💾 Saving to Firestore...');

      // Save new teams and delete old keys
      await setDoc(settingsRef, {
        teams: { ...newTeams, ...deletions },
        lastUpdated: new Date().toISOString(),
        migrationNote: `UUID migration completed at ${new Date().toISOString()}`
      }, { merge: true });

      addLog('✅ Migration complete!');
      addLog('🔄 Refreshing page in 2 seconds...');
      
      setStatus('complete');
      setNeedsMigration(false);
      
      // Reload the page to get fresh data
      setTimeout(() => {
        window.location.reload();
      }, 2000);

    } catch (error) {
      addLog(`❌ Error: ${error.message}`);
      setStatus('error');
    }
  };

  // Don't show anything if migration not needed (after checking)
  if (status === 'complete') {
    return null;
  }

  // Show checking indicator
  if (status === 'checking') {
    return (
      <div style={{
        padding: '16px',
        backgroundColor: '#dbeafe',
        border: '1px solid #3b82f6',
        borderRadius: '8px',
        marginBottom: '24px',
        color: '#1e40af',
        fontSize: '14px'
      }}>
        🔍 Checking if team migration is needed...
      </div>
    );
  }

  return (
    <div style={{
      padding: '20px',
      backgroundColor: '#fef3c7',
      border: '2px solid #f59e0b',
      borderRadius: '12px',
      marginBottom: '24px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
      }}>
        <span style={{ fontSize: '24px' }}>⚠️</span>
        <div>
          <h3 style={{ margin: 0, color: '#92400e', fontSize: '18px' }}>
            Team Database Migration Required
          </h3>
          <p style={{ margin: '4px 0 0 0', color: '#a16207', fontSize: '14px' }}>
            {teamsToMigrate} team{teamsToMigrate !== 1 ? 's' : ''} need to be migrated to the new UUID format.
            This is a one-time operation that enables safe team renaming.
          </p>
        </div>
      </div>

      {status === 'needed' && (
        <button
          onClick={runMigration}
          style={{
            padding: '12px 24px',
            backgroundColor: '#10b981',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '600'
          }}
        >
          Run Migration Now
        </button>
      )}

      {status === 'migrating' && (
        <div style={{
          padding: '4px 12px',
          backgroundColor: '#dbeafe',
          color: '#1e40af',
          borderRadius: '6px',
          display: 'inline-block',
          fontSize: '14px'
        }}>
          Migrating...
        </div>
      )}

      {logs.length > 0 && (
        <div style={{
          marginTop: '16px',
          padding: '12px',
          backgroundColor: '#1f2937',
          borderRadius: '8px',
          fontFamily: 'monospace',
          fontSize: '12px',
          color: '#e5e7eb',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {logs.map((log, i) => (
            <div key={i} style={{ marginBottom: '4px' }}>{log}</div>
          ))}
        </div>
      )}

      {status === 'error' && (
        <p style={{ color: '#dc2626', marginTop: '12px', fontSize: '14px' }}>
          Migration failed. Please try again or contact support.
        </p>
      )}
    </div>
  );
};

// =============================================================================
// UUID GENERATOR - Creates stable unique IDs for teams
// =============================================================================
const generateUUID = () => {
  return 'team_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
};

// =============================================================================
// HELPER FUNCTIONS FOR TEAM FORMAT CONVERSION (FIXED - UUID BASED)
// =============================================================================

/**
 * Convert teams object (Firestore format) to array (React state format)
 * Handles both old array format, old name-based object format, and new UUID format
 * ENSURES every team has a stable ID
 */
const convertTeamsToArray = (teamsData) => {
  if (!teamsData) return [];
  
  // Already an array (old format or already converted)
  if (Array.isArray(teamsData)) {
    // Ensure each team has an ID
    return teamsData.map(team => ({
      ...team,
      id: team.id || generateUUID()
    }));
  }
  
  // Object format (could be name-based or UUID-based)
  if (typeof teamsData === 'object') {
    return Object.entries(teamsData).map(([key, team]) => {
      // If the key looks like a UUID (starts with 'team_'), use it
      // Otherwise, generate a new UUID and preserve the team data
      const isUUID = key.startsWith('team_');
      return {
        ...team,
        id: isUUID ? key : (team.id || generateUUID())
      };
    });
  }
  
  return [];
};

/**
 * Convert teams array (React state) to object with UUIDs as keys (Firestore format)
 * This ensures teams have STABLE keys that don't change when names are edited
 */
const convertTeamsToObject = (teamsArray) => {
  if (!Array.isArray(teamsArray)) return {};
  
  const teamsObject = {};
  teamsArray.forEach(team => {
    if (team.name && team.name.trim()) {
      // Use the team's stable ID as the key (generate one if missing)
      const teamKey = team.id || generateUUID();
      teamsObject[teamKey] = {
        ...team,
        id: teamKey, // Ensure ID is stored in the team object too
        name: team.name.trim()
      };
    }
  });
  
  return teamsObject;
};

/**
 * Validate team name uniqueness (case-insensitive)
 */
const validateUniqueTeamName = (teamName, existingTeams, excludeTeamId = null) => {
  const trimmedName = teamName.trim().toLowerCase();
  
  return !existingTeams.some(team => 
    team.name.toLowerCase() === trimmedName && 
    team.id !== excludeTeamId
  );
};

// =============================================================================
// MOBILE RESTRICTION MESSAGE
// =============================================================================
const MobileRestrictionMessage = ({ branding, clubName }) => (
  <div style={{
    minHeight: '100vh',
    background: branding?.primaryColor && branding?.secondaryColor 
      ? `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`
      : 'linear-gradient(135deg, #243665 0%, #3b82f6 100%)',
    fontFamily: 'system-ui, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '40px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      maxWidth: '500px',
      textAlign: 'center',
      border: '2px solid #f59e0b'
    }}>
      {branding?.clubLogoUrl && (
        <div style={{ marginBottom: '20px' }}>
          <img 
            src={branding.clubLogoUrl}
            alt={`${clubName} Logo`}
            style={{
              maxHeight: '80px',
              maxWidth: '200px',
              objectFit: 'contain'
            }}
          />
        </div>
      )}
      
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>📱</div>
      
      <h1 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#1f2937',
        marginBottom: '16px'
      }}>
        Mobile Viewing Mode
      </h1>
      
      <p style={{
        fontSize: '16px',
        color: '#6b7280',
        lineHeight: '1.6',
        marginBottom: '20px'
      }}>
        Settings configuration requires a desktop computer for the best experience. You can view your current settings, but editing is not available on mobile devices.
      </p>
      
      <button
        onClick={() => window.location.href = '/menu'}
        style={{
          padding: '12px 24px',
          backgroundColor: branding?.primaryColor || '#243665',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        Return to Menu
      </button>
    </div>
  </div>
);

// =============================================================================
// ACCESS DENIED MESSAGE - For non-admin users
// =============================================================================
const AccessDeniedMessage = ({ branding, clubName, onReturn }) => (
  <div style={{
    minHeight: '100vh',
    background: branding?.primaryColor && branding?.secondaryColor 
      ? `linear-gradient(135deg, ${branding.primaryColor} 0%, ${branding.secondaryColor} 100%)`
      : 'linear-gradient(135deg, #243665 0%, #3b82f6 100%)',
    fontFamily: 'system-ui, sans-serif',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  }}>
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '40px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      maxWidth: '500px',
      textAlign: 'center',
      border: '2px solid #dc2626'
    }}>
      {branding?.clubLogoUrl && (
        <div style={{ marginBottom: '20px' }}>
          <img 
            src={branding.clubLogoUrl}
            alt={`${clubName} Logo`}
            style={{
              maxHeight: '80px',
              maxWidth: '200px',
              objectFit: 'contain'
            }}
          />
        </div>
      )}
      
      <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔒</div>
      
      <h1 style={{
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#1f2937',
        marginBottom: '16px'
      }}>
        Admin Access Required
      </h1>
      
      <p style={{
        fontSize: '16px',
        color: '#6b7280',
        lineHeight: '1.6',
        marginBottom: '20px'
      }}>
        Settings are only accessible to club administrators. If you believe you should have admin access, please contact your club administrator.
      </p>
      
      <button
        onClick={onReturn}
        style={{
          padding: '12px 24px',
          backgroundColor: branding?.primaryColor || '#243665',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        Return to Menu
      </button>
    </div>
  </div>
);

// =============================================================================
// HELPER: Update allocations when team name/color changes
// =============================================================================
const updateAllocationsForTeamChanges = async (clubId, oldTeams, newTeams, pitchNames) => {
  if (!clubId || !oldTeams || !newTeams) return { updated: 0, errors: [] };

  // Find teams that changed (comparing by UUID)
  const changedTeams = [];
  
  newTeams.forEach(newTeam => {
    const oldTeam = oldTeams.find(t => t.id === newTeam.id);
    if (oldTeam) {
      const nameChanged = oldTeam.name !== newTeam.name;
      const colorChanged = oldTeam.color !== newTeam.color;
      
      if (nameChanged || colorChanged) {
        changedTeams.push({
          id: newTeam.id,
          oldName: oldTeam.name,
          newName: newTeam.name,
          oldColor: oldTeam.color,
          newColor: newTeam.color,
          nameChanged,
          colorChanged
        });
      }
    }
  });

  if (changedTeams.length === 0) {
    console.log('[Settings] No team changes detected');
    return { updated: 0, errors: [] };
  }

  console.log('[Settings] Teams changed:', changedTeams);

  let totalUpdated = 0;
  const errors = [];

  try {
    // Get all pitch IDs from pitchNames
    const pitchIds = Object.keys(pitchNames || {});
    
    if (pitchIds.length === 0) {
      console.log('[Settings] No pitches found, skipping allocation update');
      return { updated: 0, errors: [] };
    }

    // For each pitch, check allocations and update if needed
    for (const pitchId of pitchIds) {
      try {
        const allocationsRef = collection(db, 'clubs', clubId, 'pitches', pitchId, 'allocations');
        const allocationsSnap = await getDocs(allocationsRef);
        
        if (allocationsSnap.empty) continue;

        const batch = writeBatch(db);
        let batchCount = 0;

        allocationsSnap.docs.forEach(docSnap => {
          const allocation = docSnap.data();
          const teamId = allocation.teamId;
          
          // Check if this allocation's team was changed
          const changedTeam = changedTeams.find(t => 
            t.id === teamId || 
            t.oldName === allocation.teamName || 
            t.oldName === allocation.team
          );
          
          if (changedTeam) {
            const updates = {};
            
            if (changedTeam.nameChanged) {
              updates.teamName = changedTeam.newName;
              updates.team = changedTeam.newName;
            }
            
            if (changedTeam.colorChanged) {
              updates.color = changedTeam.newColor;
              updates.colour = changedTeam.newColor;
            }
            
            if (Object.keys(updates).length > 0) {
              batch.update(docSnap.ref, updates);
              batchCount++;
            }
          }
        });

        if (batchCount > 0) {
          await batch.commit();
          totalUpdated += batchCount;
          console.log(`[Settings] Updated ${batchCount} allocations in ${pitchId}`);
        }
      } catch (pitchError) {
        console.error(`[Settings] Error updating allocations for ${pitchId}:`, pitchError);
        errors.push({ pitch: pitchId, error: pitchError.message });
      }
    }

    console.log(`[Settings] Total allocations updated: ${totalUpdated}`);
    return { updated: totalUpdated, errors };
    
  } catch (error) {
    console.error('[Settings] Error updating allocations:', error);
    return { updated: totalUpdated, errors: [...errors, { general: error.message }] };
  }
};

// =============================================================================
// MAIN SETTINGS COMPONENT
// =============================================================================
const Settings = () => {
  const navigate = useNavigate();
  
  // ===========================================================================
  // STATE
  // ===========================================================================
  const [user, setUser] = useState(null);
  const [clubInfo, setClubInfo] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [teams, setTeams] = useState([]);
  const [pitchNames, setPitchNames] = useState({});
  const [pitchReferencePoints, setPitchReferencePoints] = useState({});
  const [pitchSectionConfigurations, setPitchSectionConfigurations] = useState({});
  const [configuredPitches, setConfiguredPitches] = useState([]);
  const [branding, setBranding] = useState(null);
  const [satelliteConfig, setSatelliteConfig] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState('free');
  
  // Ref to track teams BEFORE edit starts (for allocation updates)
  const previousTeamsRef = useRef([]);
  
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isLoadingRole, setIsLoadingRole] = useState(true);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  const isAdmin = userRole === 'admin';
  
  // ===========================================================================
  // DEBUG: Log teams whenever they change
  // ===========================================================================
  useEffect(() => {
    console.log('🔍 Teams state changed:', teams);
    console.log('🔍 Teams length:', teams.length);
    // Log team IDs for debugging
    if (teams.length > 0) {
      console.log('🔑 Team IDs:', teams.map(t => ({ id: t.id, name: t.name })));
    }
  }, [teams]);
  
  // ===========================================================================
  // DEBUG: Log configured pitches whenever they change
  // ===========================================================================
  useEffect(() => {
    console.log('🏟️ Configured pitches changed:', configuredPitches);
    console.log('🏟️ Number of pitches:', configuredPitches.length);
  }, [configuredPitches]);
  
  // ===========================================================================
  // DEBUG: Log role changes
  // ===========================================================================
  useEffect(() => {
    console.log('👤 User role changed:', userRole, '| isAdmin:', userRole === 'admin');
  }, [userRole]);
  
  // ===========================================================================
  // EFFECTS
  // ===========================================================================
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        console.log('🔐 User authenticated:', currentUser.uid);
        setUser(currentUser);
        await loadUserAndClubData(currentUser);
      } else {
        console.log('❌ No user authenticated, redirecting to login');
        navigate('/login');
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);
  
  // Load settings when clubInfo is available AND user is admin
  useEffect(() => {
    console.log('📋 ClubInfo changed:', clubInfo);
    if (clubInfo?.clubId && isAdmin) {
      console.log('✅ ClubId available and user is admin, loading settings...');
      loadSettingsFromFirestore();
    } else if (clubInfo?.clubId && !isAdmin && !isLoadingRole) {
      console.log('⚠️ User is not admin, skipping settings load');
      setIsLoadingSettings(false);
    }
  }, [clubInfo?.clubId, isAdmin, isLoadingRole]);
  
  // ===========================================================================
  // HANDLERS: Load User and Club Data
  // ===========================================================================
  const loadUserAndClubData = async (currentUser) => {
    setIsLoadingRole(true);
    try {
      console.log('📥 Loading user and club data...');
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('✅ User data loaded:', userData);
        
        const role = userData.role || 'viewer';
        setUserRole(role);
        console.log('👤 User role set to:', role);
        
        if (userData.clubId) {
          console.log('🏢 Club ID found:', userData.clubId);
          
          const clubDocRef = doc(db, 'clubs', userData.clubId);
          const clubDoc = await getDoc(clubDocRef);
          
          let clubName = 'Unknown Club';
          let tier = 'free';
          let trialEndsAt = null;
          let subscriptionStatus = 'trial';
          let primaryFacilityId = null;
          const pitches = [];
          
          if (clubDoc.exists()) {
            const clubData = clubDoc.data();
            clubName = clubData.clubName || clubData.name || 'Unknown Club';
            tier = clubData.subscriptionTier || 'free';
            trialEndsAt = clubData.trialEndsAt || null;
            subscriptionStatus = clubData.subscriptionStatus || 'trial';
            primaryFacilityId = clubData.primaryFacilityId || null;
            console.log('🏢 Club name loaded:', clubName);
            console.log('📊 Subscription tier loaded:', tier);
            console.log('⏰ Trial ends at:', trialEndsAt);
            console.log('📋 Subscription status:', subscriptionStatus);
            console.log('🏟️ Primary facility ID:', primaryFacilityId);
            setSubscriptionTier(tier);
            
            const loadedPitches = clubData.pitches || [];
            console.log('🛰️ Pitches loaded from club document:', loadedPitches);
            console.log('🛰️ Number of pitches found:', loadedPitches.length);
            
            const normalizedPitches = loadedPitches.map((pitch, index) => {
              let pitchId = pitch.id || pitch.pitchId || `pitch-${index + 1}`;
              
              if (pitchId.startsWith('pitch-pitch-')) {
                pitchId = pitchId.replace('pitch-pitch-', 'pitch-');
              }
              
              return {
                ...pitch,
                id: pitchId,
                name: pitch.name || `Pitch ${index + 1}`
              };
            });
            
            console.log('🔧 Normalized pitches:', normalizedPitches);
            setConfiguredPitches(normalizedPitches);
            
            if (clubData.branding) {
              setBranding(clubData.branding);
            }
          }
          
          setClubInfo({
            clubId: userData.clubId,
            name: clubName,
            trialEndsAt: trialEndsAt,
            subscriptionStatus: subscriptionStatus,
            primaryFacilityId: primaryFacilityId,
            hasCompletedSetup: userData.hasCompletedSetup || false
          });
          console.log('✅ Club info set:', { 
            clubId: userData.clubId, 
            name: clubName,
            trialEndsAt: trialEndsAt,
            subscriptionStatus: subscriptionStatus,
            primaryFacilityId: primaryFacilityId,
            hasCompletedSetup: userData.hasCompletedSetup || false
          });
        }
      } else {
        setUserRole('viewer');
      }
    } catch (error) {
      console.error('❌ Error loading user and club data:', error);
      setUserRole('viewer');
    } finally {
      setIsLoadingRole(false);
    }
  };
  
  // ===========================================================================
  // HANDLERS: Load Settings from Firestore
  // ===========================================================================
  const loadSettingsFromFirestore = async () => {
    if (!clubInfo?.clubId) {
      console.log('⚠️ No club ID available, skipping settings load');
      return;
    }
    
    try {
      console.log('📥 Loading settings from Firestore for club:', clubInfo.clubId);
      setIsLoadingSettings(true);
      
      const settingsRef = doc(db, 'clubs', clubInfo.clubId, 'settings', 'general');
      const settingsSnap = await getDoc(settingsRef);
      
      if (settingsSnap.exists()) {
        const data = settingsSnap.data();
        console.log('📦 Settings data loaded:', data);
        
        // Load branding
        if (data.branding) {
          const brandingData = {
            clubLogoUrl: data.branding.clubLogoUrl || null,
            primaryColor: data.branding.primaryColor || '#004433',
            secondaryColor: data.branding.secondaryColor || '#3b82f6',
            sponsorLogoUrl: data.branding.sponsorLogoUrl || null,
            sponsorUrl: data.branding.sponsorUrl || ''
          };
          console.log('🎨 Setting branding:', brandingData);
          setBranding(brandingData);
        } else {
          console.log('⚠️ No branding data found');
        }
        
        // =====================================================================
        // LOAD TEAMS - HANDLE ALL FORMATS AND ENSURE STABLE IDs
        // =====================================================================
        if (data.teams) {
          console.log('👥 Raw teams data from Firestore:', data.teams);
          console.log('👥 Teams data type:', Array.isArray(data.teams) ? 'array' : typeof data.teams);
          
          // Convert to array with stable IDs
          const teamsArray = convertTeamsToArray(data.teams);
          
          // Apply defaults
          const teamsWithDefaults = teamsArray.map(team => ({
            id: team.id || generateUUID(), // CRITICAL: Ensure every team has an ID
            name: team.name || 'Unnamed Team',
            color: team.color || '#3b82f6',
            matchArea: team.matchArea || 'FULL',
            matchDuration: team.matchDuration || 60
          }));
          
          console.log('👥 Converted teams to array:', teamsWithDefaults);
          console.log('👥 Number of teams:', teamsWithDefaults.length);
          console.log('🔑 Team IDs assigned:', teamsWithDefaults.map(t => t.id));
          setTeams(teamsWithDefaults);
          // Store as "last saved" state for allocation update comparison
          previousTeamsRef.current = JSON.parse(JSON.stringify(teamsWithDefaults));
        } else {
          console.log('⚠️ No teams found');
          setTeams([]);
          previousTeamsRef.current = [];
        }
        
        // Load pitch data
        if (data.pitchNames) {
          console.log('🏟️ Setting pitch names:', data.pitchNames);
          setPitchNames(data.pitchNames);
          
          const pitchList = Object.keys(data.pitchNames).map((pitchId, index) => ({
            id: pitchId,
            name: data.pitchNames[pitchId] || `Pitch ${index + 1}`
          }));
          console.log('🏟️ Generated pitch list from pitchNames:', pitchList);
          
          if (configuredPitches.length === 0) {
            setConfiguredPitches(pitchList);
            console.log('🏟️ Set configured pitches from pitch names:', pitchList);
          }
        }
        
        if (data.pitchReferencePoints) {
          console.log('📍 Setting reference points:', data.pitchReferencePoints);
          setPitchReferencePoints(data.pitchReferencePoints);
        }
        
        if (data.pitchSectionConfigurations) {
          console.log('⚙️ Setting section configurations:', data.pitchSectionConfigurations);
          setPitchSectionConfigurations(data.pitchSectionConfigurations);
        }
      } else {
        console.log('⚠️ Settings document does not exist, using defaults');
      }
    } catch (error) {
      console.error('❌ Error loading settings:', error);
      setErrors({ load: 'Failed to load settings. Please refresh the page.' });
    } finally {
      setIsLoadingSettings(false);
      console.log('✅ Finished loading settings');
    }
  };
  
  // ===========================================================================
  // HANDLERS: Save Settings to Firestore (FIXED - UUID BASED)
  // ===========================================================================
  const saveSettingsToFirestore = async (updatedData) => {
    if (!clubInfo?.clubId) {
      console.error('❌ Cannot save: No club ID available');
      return false;
    }
    
    // Prevent double-saves
    if (isSavingSettings) {
      console.log('⚠️ Save already in progress, skipping duplicate save');
      return false;
    }
    
    // =========================================================================
    // CONVERT TEAMS ARRAY TO OBJECT WITH UUIDs BEFORE SAVING
    // =========================================================================
    const dataToSave = { ...updatedData };
    
    if (dataToSave.teams && Array.isArray(dataToSave.teams)) {
      console.log('🔄 Converting teams array to object format for Firestore...');
      console.log('📊 Teams before conversion:', dataToSave.teams);
      console.log('🔑 Team IDs before conversion:', dataToSave.teams.map(t => ({ id: t.id, name: t.name })));
      
      dataToSave.teams = convertTeamsToObject(dataToSave.teams);
      
      console.log('✅ Teams after conversion:', dataToSave.teams);
      console.log('🔑 Team keys (UUIDs):', Object.keys(dataToSave.teams));
    }
    
    // Remove undefined values from data before saving to Firestore
    const cleanData = {};
    for (const [key, value] of Object.entries(dataToSave)) {
      if (value !== undefined) {
        cleanData[key] = value;
      }
    }
    
    console.log('💾 Saving settings to Firestore...', cleanData);
    setIsSavingSettings(true);
    
    try {
      const settingsRef = doc(db, 'clubs', clubInfo.clubId, 'settings', 'general');
      
      // First, get current teams to find which ones were deleted
      const currentDoc = await getDoc(settingsRef);
      const currentData = currentDoc.exists() ? currentDoc.data() : {};
      const currentTeams = currentData.teams || {};
      
      // Find deleted teams (keys that exist in Firestore but not in new data)
      const newTeamKeys = cleanData.teams ? Object.keys(cleanData.teams) : [];
      const currentTeamKeys = typeof currentTeams === 'object' && !Array.isArray(currentTeams) 
        ? Object.keys(currentTeams) 
        : [];
      
      const deletedTeamKeys = currentTeamKeys.filter(key => !newTeamKeys.includes(key));
      
      if (deletedTeamKeys.length > 0) {
        console.log('🗑️ Teams to delete (by UUID):', deletedTeamKeys);
        // Log which teams are being deleted for debugging
        deletedTeamKeys.forEach(key => {
          const teamName = currentTeams[key]?.name || 'Unknown';
          console.log(`   - Deleting: ${key} (${teamName})`);
        });
        
        // Mark deleted teams with deleteField()
        deletedTeamKeys.forEach(key => {
          if (!cleanData.teams) cleanData.teams = {};
          cleanData.teams[key] = deleteField();
        });
      }
      
      await setDoc(settingsRef, {
        ...cleanData,
        lastUpdated: new Date().toISOString()
      }, { merge: true });
      
      console.log('✅ Settings saved successfully');
      return true;
    } catch (error) {
      console.error('❌ Error saving settings:', error);
      setErrors({ save: 'Failed to save settings. Please try again.' });
      return false;
    } finally {
      setIsSavingSettings(false);
    }
  };
  
  // ===========================================================================
  // HANDLERS: Success Message
  // ===========================================================================
  const showSuccess = useCallback((message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  }, []);
  
  // ===========================================================================
  // HANDLERS: Logout
  // ===========================================================================
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };
  
  // ===========================================================================
  // RENDER: Loading States
  // ===========================================================================
  if (isLoadingRole) {
    return <SettingsSkeleton />;
  }
  
  // Check for non-admin AFTER role is loaded
  if (!isAdmin) {
    return (
      <AccessDeniedMessage 
        branding={branding}
        clubName={clubInfo?.name}
        onReturn={() => navigate('/menu')}
      />
    );
  }
  
  if (isMobile) {
    return (
      <MobileRestrictionMessage 
        branding={branding}
        clubName={clubInfo?.name}
      />
    );
  }
  
  if (isLoadingSettings) {
    return <SettingsSkeleton />;
  }
  
  // ===========================================================================
  // RENDER: Main Content
  // ===========================================================================
  return (
    <div style={{ 
      display: 'flex',
      minHeight: '100vh',
      fontFamily: 'system-ui, sans-serif'
    }}>
      {/* ===================================================================
          SIDEBAR
          =================================================================== */}
      <div style={{
        width: '250px',
        backgroundColor: '#243665',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        position: 'sticky',
        top: 0,
        height: '100vh'
      }}>
        {/* Pitchallo Logo */}
        <div style={{
          padding: '16px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          display: 'flex',
          justifyContent: 'center'
        }}>
          <img 
            src={logo}
            alt="Pitchallo Logo"
            style={{
              width: '100%',
              maxWidth: '180px',
              height: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>
        
        {/* Branded Club Info Card */}
        <div style={{ padding: '12px' }}>
          <div style={{
            background: `linear-gradient(135deg, ${branding?.primaryColor || '#10b981'} 0%, ${branding?.secondaryColor || '#3b82f6'} 100%)`,
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
          }}>
            {/* Club Logo + Name Row */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '6px'
            }}>
              {branding?.clubLogoUrl && (
                <img 
                  src={branding.clubLogoUrl} 
                  alt="Club Logo" 
                  style={{
                    height: '58px',
                    width: '58px',
                    objectFit: 'contain',
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    flexShrink: 0
                  }}
                />
              )}
              <div style={{
                fontSize: '17px',
                fontWeight: '600',
                color: 'white',
                lineHeight: '1.3'
              }}>
                {clubInfo?.name || 'Settings'}
              </div>
            </div>
            <div style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.7)',
              marginBottom: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {user?.email || 'Not logged in'}
            </div>
            <div style={{
              fontSize: '11px',
              color: 'white',
              padding: '4px 10px',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '20px',
              display: 'inline-block',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {userRole === 'admin' ? 'Administrator' : userRole === 'viewer' ? 'Viewer' : 'User'}
            </div>
          </div>
        </div>
        
        {/* Navigation - empty now, Home Menu moved to bottom */}
        <div style={{ 
          flex: 1,
          padding: '12px',
          overflowY: 'auto'
        }}>
        </div>

        {/* Bottom Actions */}
        <div style={{ padding: '12px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          
          <button
            onClick={() => navigate('/menu')}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.15s ease',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
          >
            Home Menu
          </button>

          <button
            onClick={() => setShowFeedbackModal(true)}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.15s ease',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
          >
            Feedback
          </button>

          <button
            onClick={() => navigate('/changelog')}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.15s ease',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
          >
            Change Log
          </button>

          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'rgba(255,255,255,0.9)',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.15s ease',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* ===================================================================
          MAIN CONTENT
          =================================================================== */}
      <div style={{ 
        flex: 1,
        padding: clubInfo?.subscriptionStatus === 'trial' ? '80px 24px 24px 24px' : '24px',
        overflowY: 'auto',
        height: '100vh'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            <div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: '#1f2937',
                margin: '0'
              }}>
                Settings
              </h1>
              {clubInfo && (
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  marginTop: '4px'
                }}>
                  Configure teams and pitch settings for {clubInfo.name}
                </p>
              )}
            </div>
          </div>
          
          {/* Status Messages */}
          {isSavingSettings && (
            <div style={{
              backgroundColor: '#dbeafe',
              border: '1px solid #3b82f6',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              color: '#1e40af',
              fontSize: '14px'
            }}>
              Saving settings to cloud...
            </div>
          )}

          {successMessage && (
            <div style={{
              backgroundColor: '#d1fae5',
              border: '1px solid #34d399',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              color: '#065f46',
              fontSize: '14px'
            }}>
              {successMessage}
            </div>
          )}
          
          {/* MIGRATION TOOL - Shows only when migration is needed */}
          <TeamMigrationTool 
            clubId={clubInfo?.clubId}
            onMigrationComplete={() => loadSettingsFromFirestore()}
          />
          
          {/* Components */}
          <ClubProfile
            clubInfo={clubInfo}
            branding={branding}
            onBrandingChange={setBranding}
            onSave={async (updatedBranding) => {
              return await saveSettingsToFirestore({
                teams,
                pitchNames,
                pitchReferencePoints,
                pitchSectionConfigurations,
                branding: updatedBranding
              });
            }}
            showSuccess={showSuccess}
            isSaving={isSavingSettings}
            isAdmin={isAdmin}
          />

          <UserManagement
            clubInfo={clubInfo}
            showSuccess={showSuccess}
            isAdmin={isAdmin}
            subscriptionTier={subscriptionTier}
          />

          <TeamManagement
            teams={teams}
            onTeamsChange={setTeams}
            onSave={async (updatedTeams) => {
              // Use previousTeamsRef which holds the "last saved" state
              // This is important because onTeamsChange updates `teams` state BEFORE onSave is called
              const oldTeams = previousTeamsRef.current;
              
              // Save to Firestore
              const saveResult = await saveSettingsToFirestore({
                teams: updatedTeams,
                pitchNames,
                pitchReferencePoints,
                pitchSectionConfigurations,
                branding
              });
              
              // If save successful, update allocations for any changed teams
              if (saveResult && clubInfo?.clubId) {
                try {
                  const { updated, errors } = await updateAllocationsForTeamChanges(
                    clubInfo.clubId,
                    oldTeams,
                    updatedTeams,
                    pitchNames
                  );
                  
                  if (updated > 0) {
                    console.log(`[Settings] ✅ Updated ${updated} allocation(s) with new team info`);
                  }
                  if (errors.length > 0) {
                    console.warn('[Settings] Some allocation updates failed:', errors);
                  }
                } catch (allocError) {
                  console.error('[Settings] Error updating allocations:', allocError);
                  // Don't fail the save if allocation update fails
                }
                
                // Update previousTeamsRef to the new saved state
                previousTeamsRef.current = JSON.parse(JSON.stringify(updatedTeams));
              }
              
              return saveResult;
            }}
            showSuccess={showSuccess}
            isSaving={isSavingSettings}
            clubInfo={clubInfo}
            subscriptionTier={subscriptionTier}
          />

          {/* ===============================================================
              FACILITY MANAGEMENT - Multi-Facility Support
              =============================================================== */}
          <FacilityManagement
            clubInfo={clubInfo}
            showSuccess={showSuccess}
            isAdmin={isAdmin}
            subscriptionTier={subscriptionTier}
          />

          <PitchConfiguration
            configuredPitches={configuredPitches}
            pitchNames={pitchNames}
            pitchReferencePoints={pitchReferencePoints}
            pitchSectionConfigurations={pitchSectionConfigurations}
            onPitchNamesChange={setPitchNames}
            onReferencePointsChange={setPitchReferencePoints}
            onSectionConfigurationsChange={setPitchSectionConfigurations}
            onSave={async (names, refPoints, sectionConfigs, metadata) => {
              return await saveSettingsToFirestore({
                teams,
                pitchNames: names,
                pitchReferencePoints: refPoints,
                pitchSectionConfigurations: sectionConfigs,
                branding,
                ...metadata
              });
            }}
            showSuccess={showSuccess}
            isSaving={isSavingSettings}
            isAdmin={isAdmin}
            user={user}
            clubInfo={clubInfo}
            subscriptionTier={subscriptionTier}
          />

          <SharedFacility
            clubId={clubInfo?.clubId}
            clubInfo={clubInfo}
            userEmail={user?.email}
            hasCompletedSetup={clubInfo?.hasCompletedSetup || false}
          />

          <SubscriptionManagement
            clubInfo={clubInfo}
            subscriptionTier={subscriptionTier}
            subscriptionStatus={clubInfo?.subscriptionStatus || 'trial'}
            showSuccess={showSuccess}
            isAdmin={isAdmin}
          />

          <SupportContact
            clubInfo={clubInfo}
            subscriptionTier={subscriptionTier}
            subscriptionStatus={clubInfo?.subscriptionStatus || 'trial'}
            user={user}
            showSuccess={showSuccess}
            isAdmin={isAdmin}
          />
        </div>
      </div>
      
      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        userEmail={user?.email}
        clubName={clubInfo?.name}
        onSubmitSuccess={() => showSuccess('Feedback sent successfully')}
      />

    </div>
  );
};

export default Settings;
